import {
  Box,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
} from "@chakra-ui/react";
import { InfoIcon } from "@root/assets/icons/info-icon-login";
import { LoginBrandingLogo, LoginBrandingTitle } from "@root/components/branding";

import {
  LinkText,
  //StyledCheckboxText,
  //StyledInputField,
  AuthFormContainer,
  AuthCardEllipse,
  AuthCardTopFrame,
  AuthHeading,
  AuthSubheading,
  AuthFieldsWrapper,
  AuthInputBox,
  AuthInputField,
  AuthLabelText,
  AuthButton,
  AuthButtonWrapper,
  AuthLinkText,
  AuthCheckboxText,
  AddNoticeButton,
} from "@root/commonStyles/commonStyle";
//import { PasswordInput } from "@root/components/common/passwordInput/passwordInput";
import { ROUTE_NAMES, USER_TYPES } from "@root/constants";
import { login } from "@store/thunks";
import { Field, Formik } from "formik";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FloatingInput } from "./floating-input";
import { LanguageToggle } from "@root/components/dashboard/sidebar/languagetoggler";

export const Login: React.FC = () => {
  const { t } = useTranslation("common");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { pathname } = useLocation();
  // const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const initialValues = {
    email: "",
    password: "",
    rememberMe: false,
  };
  const validate = (values: LoginValues) => {
    const errors: Partial<LoginValues> = {};

    if (!values.password) {
      errors.password = t("login.requiredPassword");
    } else if (values.password.length < 6) {
      errors.password = t("login.passwordValidation");
    }
    return errors;
  };
  // Shift Admin here
  const onSuccess = (res: any) => {
    if (!res?.token) {
      return;
    }
    setLoading(false);
    if (res?.user?.roleId === USER_TYPES.ADMIN || res?.user?.roleId === USER_TYPES.ORGANIZATION_ADMIN) {
      navigate(ROUTE_NAMES.ADMIN_USER_MANAGEMENT);
    } else {
      navigate(ROUTE_NAMES.DASHBOARD);
    }
  };
  const onError = () => {
    setLoading(false);
  };

  const onSubmit = (values: any) => {
    setLoading(true);
    try {
      const payload = {
        password: values.password,
        email: values.email,
        // captcha_token: captchaToken,
      };
      dispatch(
        login({
          payload,
          onSuccess,
          onError,
        })
      );
    } catch (error) { }
  };

  // const handleCaptchaChange = (token: string | null) => {
  //   setCaptchaToken(token);
  // };

  return (
    <Flex
      minH="100vh"
      width="100%"
      bg="#F5F5F5"
      align="center"
      justify="center"
      position="relative"
    >
      <Box position="relative">
        <Box position="absolute" top="0" right="0" zIndex={1000}>
          <LanguageToggle variant="public" position="relative" />
        </Box>
        <AuthFormContainer minWidth={{ base: "auto", md: "550px" }}>
          <AuthCardEllipse />

          {/* Top Section */}
          <AuthCardTopFrame>
            <LoginBrandingLogo />
            <LoginBrandingTitle />
          </AuthCardTopFrame>

          {/* Form */}
          <Formik
            validate={validate}
            initialValues={initialValues}
            onSubmit={onSubmit}
          >
            {({ handleSubmit, errors, touched, values, setFieldValue }) => (
              <form onSubmit={handleSubmit} style={{ width: "100%" }}>
                <AuthFieldsWrapper>
                  <Flex
                    direction="column"
                    align="flex-start"
                    gap="1rem"
                    width="100%"
                  >
                    {/* ID / User Input */}
                    <Flex direction="column" gap="0.5rem" width="100%">
                      <FloatingInput
                        id="email"
                        label={t("admin.userId")}
                        name="email"
                        value={values.email}
                        error={errors.email}
                        touched={touched.email}
                        onChange={(e) => setFieldValue("email", e.target.value)}
                      />
                    </Flex>

                    {/* Password */}
                    <Flex direction="column" gap="0.5rem" width="100%">
                      <FloatingInput
                        id="password"
                        label={t("login.password")}
                        name="password"
                        type="password"
                        value={values.password}
                        error={errors.password}
                        touched={touched.password}
                        onChange={(e) =>
                          setFieldValue("password", e.target.value)
                        }
                      />

                      {/* Easy Login + Forgot Password */}
                      <Flex
                        justify="space-between"
                        align="center"
                        width="100%"
                        mt={4}
                      >
                        <Flex align="center" gap="0.35rem">
                          <Field
                            as={Checkbox}
                            id="easyLogin"
                            name="easyLogin"
                            iconSize="18px"
                            size="lg"
                            borderRadius="50%"
                            sx={{
                              ".chakra-checkbox__control": {
                                borderRadius: "50%", // Round Shape
                                width: "20px",
                                height: "20px",
                                border: "1px solid #000",
                              },
                              ".chakra-checkbox__control[data-checked]": {
                                backgroundColor: "#000", // Black fill when checked
                                borderColor: "#000",
                              },
                              ".chakra-checkbox__control[data-checked] svg": {
                                color: "#fff", // White tick
                              },
                            }}
                          />
                          <AuthCheckboxText>
                            {t("login.easyLogin")}
                          </AuthCheckboxText>
                          <InfoIcon />
                        </Flex>

                        <AuthLinkText
                          onClick={() => navigate(ROUTE_NAMES.FORGET_PASSWORD)}
                          whiteSpace="nowrap"
                        >
                          {t("forgetPassword.forgetPassword")}
                        </AuthLinkText>
                      </Flex>
                    </Flex>
                  </Flex>
                </AuthFieldsWrapper>

                {/* Button */}
                <AuthButtonWrapper>
                  <AuthButton
                    isLoading={loading}
                    isDisabled={!values.email || !values.password}
                    bg={!values.email || !values.password ? "#EBEBEB" : "black"}
                    color={
                      !values.email || !values.password ? "#BFBFBF" : "#FFFFFF"
                    }
                    _hover={{
                      bg:
                        !values.email || !values.password
                          ? "#dbd9d9ff"
                          : "#3d3535ff",
                    }}
                    type="submit"
                  >
                    {t("login.signIn")}
                  </AuthButton>
                </AuthButtonWrapper>
              </form>
            )}
          </Formik>

          {/* Signup link (unchanged unless admin) */}
          {!pathname.includes(ROUTE_NAMES.ADMIN_LOGIN) && (
            <Flex
              justify="center"
              alignItems={"center"}
              gap="0.25rem"
              width="100%"
            >
              <AuthCheckboxText>{t("login.dontHaveAccount")}</AuthCheckboxText>
              <LinkText
                to="/signup"
                color="#3d3535ff"
                style={{ textDecoration: "underline" }}
              >
                {t("signup.signupLink")}
              </LinkText>
            </Flex>
          )}
        </AuthFormContainer>
      </Box>
    </Flex>
  );
};
